import React, { useContext, useRef } from "react";

import { StructuredTextRenderer } from "../StructuredTextRenderer";

import t from "../../lib/translate";
import { generateClassString as gcs } from "../../lib/helper/generateClassString";
import { formatMessage } from "../../lib/helper/logMessageFormat";
import GeneralPageContext from "../../templates/GeneralPageContext";
import Link from "../Link";

import * as styles from "./component.module.less";
import AccordionItem from "../Accordion/AccordionItem";
import AccordionButton from "../Accordion/AccordionButton";
import AccordionBody from "../Accordion/AccordionBody";
import Accordion from "../Accordion";
import { Button } from "../Button";

import { PhotoCredit } from "./PhotoCredit/PhotoCredit";

import LogoInfraTec from "../../../static/assets/logo-infratec.svg";
import { useIsLargeScreen } from "../../lib/hooks/mediaQueries";
import { useOnScreen } from "../../lib/hooks/useOnScreen";
import { InquiryButton } from "../InquiryButton/InquiryButton";

export default function Footer({ config, footerData }) {
    const {
        id: pageId,
        layout: { showFooterNavigation },
        stdPages: { pageImprint, pageContact, pageDataProtection },
        toggleCookieConsent
    } = useContext(GeneralPageContext);
    const isLargeScreen = useIsLargeScreen();

    const elementRef = useRef();
    const isOnScreen = useOnScreen(elementRef);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    if (!Object.keys(footerData).length) {
        console.warn(
            formatMessage(
                pageId,
                "There is no footer data provided! Footer will not be rendered."
            )
        );
        return null;
    }

    const { navigationColumn, photoCredit } = footerData;

    return (
        <footer className={styles.siteFooter} data-datocms-noindex={true}>
            <div className={styles.footerContainer}>
                <div
                    className={gcs(styles.footerContentContainer, [
                        !showFooterNavigation,
                        styles.noNavigation
                    ])}
                >
                    {showFooterNavigation && (
                        <>
                            {isLargeScreen ? (
                                (navigationColumn || []).map((box) => {
                                    return (
                                        <div
                                            key={box.id}
                                            className={
                                                styles.footerContentBlocks
                                            }
                                        >
                                            <span>{box.headline}</span>
                                            <StructuredTextRenderer
                                                data={box.link}
                                                contentElementType="Footer"
                                            />
                                        </div>
                                    );
                                })
                            ) : (
                                <Accordion className={styles.accordion}>
                                    {(navigationColumn || []).map((box) => (
                                        <AccordionItem
                                            eventKey={box.id}
                                            key={box.id}
                                        >
                                            <AccordionButton
                                                className={
                                                    styles.accordionButton
                                                }
                                            >
                                                <span>{box.headline}</span>
                                            </AccordionButton>
                                            <AccordionBody>
                                                <StructuredTextRenderer
                                                    data={box.link}
                                                    contentElementType="Footer"
                                                />
                                            </AccordionBody>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            )}
                        </>
                    )}

                    <div
                        className={gcs(
                            styles.footerContentBlocks,
                            styles.socialMediaContentBlock
                        )}
                    >
                        <div className={styles.logo}>
                            <Link to="/">
                                <img
                                    src={LogoInfraTec}
                                    alt="Logo InfraTec"
                                    loading="lazy"
                                />
                            </Link>
                        </div>

                        <div className={styles.socialMediaButtonList}>
                            {config.socialMediaChannels.map(
                                (socialMediaLink) => {
                                    const buttonClass = [
                                        styles.socialMediaButton,
                                        "icon-btn",
                                        "link"
                                    ];

                                    switch (socialMediaLink.name) {
                                        case "Facebook":
                                            buttonClass.push("btn-facebook");
                                            break;
                                        case "LinkedIn":
                                            buttonClass.push("btn-linkedin");
                                            break;
                                        case "Xing":
                                            buttonClass.push("btn-xing");
                                            break;
                                        case "YouTube":
                                            buttonClass.push("btn-youtube");
                                            break;
                                        case "WeChat":
                                            buttonClass.push("btn-wechat");
                                            break;
                                        default:
                                            return null;
                                    }

                                    return (
                                        <Link
                                            key={socialMediaLink.id}
                                            linkClass={gcs(...buttonClass)}
                                            to={socialMediaLink.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            aria-label={socialMediaLink.name}
                                        />
                                    );
                                }
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.footerTail} ref={elementRef}>
                <div className={styles.footerTailContainer}>
                    <div className={styles.footerTailBlock}>
                        <div className={styles.footerTailContent}>
                            <Link to="/">{t("page.footer.companyTitle")}</Link>
                            <button onClick={() => toggleCookieConsent(true)}>
                                {t("page.footer.cookies")}
                            </button>
                            <Link to={pageImprint.url}>
                                {t("page.footer.imprint")}
                            </Link>
                            <Link to={pageContact.url}>
                                {t("page.footer.contact")}
                            </Link>
                            <Link to={pageDataProtection.url}>
                                {t("page.footer.dataProtection")}
                            </Link>
                            <PhotoCredit items={photoCredit} />
                        </div>
                    </div>
                    <div className={styles.largeOne}>
                        ©{" "}{new Date().getFullYear().toString()}{" "}{t("page.footer.credits")}
                    </div>
                </div>
            </div>

            <div
                className={gcs(styles.fixedOverlay, [
                    isOnScreen,
                    styles.higherPosition
                ])}
            >
                <Button
                    aria-label={t("navigation.button.to.top")}
                    onClick={scrollToTop}
                    className={styles.backToTopBtn}
                    type="prev"
                    icon="arrowLeft"
                />

                <InquiryButton config={config} />
            </div>
        </footer>
    );
}
